import { useTranslations } from "next-intl";
import { css } from "styled-system/css";
import { stack } from "styled-system/patterns";

import { DefaultError } from "@/components/handle-query";
import { Skeleton } from "@/components/skeleton";
import { useAffiliateEarningsQuery } from "@/contexts/queries/affiliate-earnings";
import { useReferrerStatsQuery } from "@/contexts/queries/referrer-stats";

import { DividedBanner, KpiTile } from "../../components";
import {
  formatKpiAmps,
  formatKpiCount,
  formatKpiFloor,
  formatKpiUsd,
  kpiCoverage,
} from "../../kpi-format";

export const KpiBand = ({
  signUpsQuery,
  earningsQuery,
  isPointsMode = false,
}: {
  signUpsQuery: ReturnType<typeof useReferrerStatsQuery>;
  earningsQuery: ReturnType<typeof useAffiliateEarningsQuery>;
  /** On Points the hero already leads with Referral Amps, so this band drops that tile. */
  isPointsMode?: boolean;
}) => {
  const t = useTranslations("affiliates.overview.kpi");

  const referredUsers = earningsQuery.data?.users.items ?? [];
  // Both tiles below reduce over `items`, which the SDK requests with a hardcoded
  // `limit: "1000"`, while the `total_count` beside it is counted before the handler
  // paginates and is therefore the real figure. The largest downline measured is 17,348,
  // so for the biggest affiliates these tiles cover under 6% of their referrals. A tile
  // that cannot be exact says so rather than printing a confident number that is wrong
  // seventeenfold.
  const coverage = kpiCoverage(referredUsers.length, earningsQuery.data?.users.total_count);

  // Both figures come from `total_volume`, which `/affiliates/earnings` reads out of
  // `account_trading_stats` — a table only `mv_trading_stats_refresh` fills. On a node
  // where that refresh MV does not exist the column is 0 throughout, so both tiles read
  // empty together. That is the honest answer, and it stays consistent with the
  // "Referred volume per day" chart, which sources `account_daily_volume` instead.
  const referredVolume = referredUsers.reduce((acc, user) => acc + (user.total_volume || 0), 0);
  // `active` is the server's own "has traded" flag, and the count is restricted to direct
  // referrals. `items` carries sub-referrals (L2) too, while sign-ups counts directs only,
  // so including L2 puts a larger number next to sign-ups than signed up at all. L2 has
  // its own tile; this one is the funnel step after sign-ups.
  const referralsWhoTraded = referredUsers.filter(
    (user) => user.level === "L1" && user.active,
  ).length;

  return (
    <DividedBanner
      cells={[
        {
          children: (
            <KpiTile
              label={t("referredVolume")}
              // Raw notional overflows the cell at network scale, so headline
              // figures are compact ("$1.23M") like the points band.
              value={
                earningsQuery.data === undefined ? (
                  <Skeleton className={css({ w: "48", h: "24", rounded: "200" })} />
                ) : (
                  formatKpiFloor(formatKpiUsd(referredVolume), coverage.partial)
                )
              }
              // The only tile whose sub says more than the window, because it is the one that
              // gets mistaken for the tier's new-user volume. Those differ in three ways — the
              // window, the levels, and whether the wallet is new to Decibel — and this figure
              // being ~$9M while new-user volume reads $0 looks like a bug without the scope
              // spelled out. The Levels page carries the full explanation.
              sub={
                <>
                  {t("referredVolumeSub")}
                  {coverage.partial && (
                    <PartialNote shown={coverage.shown} total={coverage.total} />
                  )}
                </>
              }
            />
          ),
        },
        { children: <SignUpsTile query={signUpsQuery} /> },
        {
          children: (
            <KpiTile
              label={t("referralsWhoTraded")}
              value={
                earningsQuery.data === undefined ? (
                  <Skeleton className={css({ w: "48", h: "24", rounded: "200" })} />
                ) : (
                  formatKpiFloor(formatKpiCount(referralsWhoTraded), coverage.partial)
                )
              }
              sub={
                <>
                  {t("allTime")}
                  {coverage.partial && (
                    <PartialNote shown={coverage.shown} total={coverage.total} />
                  )}
                </>
              }
            />
          ),
        },
        // Referral Amps, not the account's whole Amps balance: `earnings` counts
        // only L1 + L2 credited to the affiliate. The Points page's "Total Amps"
        // is the leaderboard row, a strictly larger figure. Dropped on Points, where
        // the hero already leads with this same figure.
        ...(isPointsMode
          ? []
          : [
              {
                children: (
                  <KpiTile
                    label={t("referralAmps")}
                    value={
                      earningsQuery.data === undefined ? (
                        <Skeleton className={css({ w: "48", h: "24", rounded: "200" })} />
                      ) : (
                        formatKpiAmps(earningsQuery.data.earnings.total_amps)
                      )
                    }
                    sub={t("allTime")}
                  />
                ),
              },
            ]),
        {
          children: (
            <KpiTile
              label={t("subReferrals")}
              value={
                earningsQuery.data === undefined ? (
                  <Skeleton className={css({ w: "48", h: "24", rounded: "200" })} />
                ) : (
                  formatKpiCount(earningsQuery.data.earnings.l2_count)
                )
              }
              sub={t("allTime")}
            />
          ),
        },
      ]}
    />
  );
};

/**
 * How much of the downline the tile above actually covers.
 *
 * Sits on the sub's second reserved line, so a partial tile is the same height as an
 * exact one and the band does not jump when the count arrives.
 */
function PartialNote({ shown, total }: { shown: number; total: number }) {
  const t = useTranslations("affiliates.overview.kpi");

  return (
    <div>
      {t("partialCoverage", {
        shown: formatKpiCount(shown),
        total: formatKpiCount(total),
      })}
    </div>
  );
}

/** Sign-ups is live: referrer total_referrals, a LIFETIME count → "All-time". */
function SignUpsTile({ query }: { query: ReturnType<typeof useReferrerStatsQuery> }) {
  const t = useTranslations("affiliates.overview.kpi");

  if (query.isError) {
    return (
      <div className={stack({ gap: "8" })}>
        <div className={sectionLabelStyles}>{t("signUps")}</div>
        <DefaultError error={query.error} resetErrorBoundary={() => void query.refetch()} />
      </div>
    );
  }

  if (query.data === undefined) {
    return (
      <KpiTile
        label={t("signUps")}
        value={<Skeleton className={css({ w: "48", h: "24", rounded: "200" })} />}
        sub={t("allTime")}
      />
    );
  }

  return (
    <KpiTile
      label={t("signUps")}
      value={formatKpiCount(query.data.total_referrals)}
      sub={t("allTime")}
    />
  );
}

const sectionLabelStyles = css({
  textStyle: "highlight.sm",
  letterSpacing: "[1px]",
  textTransform: "uppercase",
  color: "text.tertiary",
});
